export type EntityType = "LLC" | "Corporation" | "Partnership" | "Sole Proprietorship" | "NGO";

export type CompanyType = "sender" | "receiver" | "freight_forwarder" | "port_authority" | "carrier" | "customs_broker" | "insurance_provider";

export type OnboardingStatus = "pending" | "kyc_in_progress" | "kyc_completed" | "active" | "suspended" | "churned";

export type UserRole = "super_admin" | "company_admin" | "operator" | "viewer";

export interface Company {
    company_id: string;
    company_name: string;
    company_type: CompanyType;
    legal_name?: string;
    tax_id?: string;
    vat_number?: string;
    eori_number?: string;
    trading_license_number?: string;
    headquarters_address?: {
        street?: string;
        street2?: string;
        city?: string;
        state_province?: string;
        postal_code?: string;
        country_code?: string;
    };
    primary_email?: string;
    primary_phone?: string;
    website?: string;
    industry?: string;
    company_size?: string;
    year_established?: number;
    onboarding_status: OnboardingStatus;
    is_active: boolean;
    is_verified: boolean;
    verified_at?: string;
    verified_by_user_id?: string;
    notes?: string;
    created_at: string;
    updated_at: string;
    deleted_at?: string;
}

export interface TeamMember {
    email: string;
    first_name?: string;
    last_name?: string;
    role: UserRole;
}

export interface CreateCompanyData {
    company_name: string;
    company_type: CompanyType;
    legal_name?: string;
    tax_id?: string;
    vat_number?: string;
    eori_number?: string;
    trading_license_number?: string;
    address_line1?: string;
    address_line2?: string;
    city?: string;
    state_province?: string;
    postal_code?: string;
    country_code?: string;
    primary_email?: string;
    primary_phone?: string;
    website?: string;
    industry?: string;
    company_size?: string;
    year_established?: number;
    is_active: boolean;
    notes?: string;
}

export interface CreateCompanyWithTeamData extends CreateCompanyData {
    team_members: TeamMember[];
}

export interface UpdateCompanyData extends Partial<CreateCompanyData> {
    company_id: string;
}

export interface CompanyFilters {
    search?: string;
    company_type?: CompanyType;
    is_active?: boolean;
    country_code?: string;
    skip?: number;
    limit?: number;
}

export interface CompanyStats {
    total: number;
    active: number;
    suspended: number;
    inactive: number;
    newThisMonth: number;
}

// Tipi per il form stepper
export interface CompanyFormStep1Data {
    company_type: CompanyType | '';
    company_name: string;
    legal_name: string;
    entityType: EntityType | '';
    tax_id: string;
    address: {
        street: string;
        city: string;
        state: string;
        postalCode: string;
    };
    country_code: string;
    primary_email: string;
    primary_phone: string;
    is_active: boolean;
    notes: string;
}

export interface CompanyFormStep2Data {
    team_members: TeamMember[];
}

export interface CompanyFormState {
    currentStep: number;
    isStepValid: Record<number, boolean>;
    step1: CompanyFormStep1Data;
    step2: CompanyFormStep2Data;
}

// Helper functions for mapping
export function mapCompanyTypeToDisplay(type: CompanyType): string {
    const mapping: Record<CompanyType, string> = {
        sender: "Shipper",
        receiver: "Receiver",
        freight_forwarder: "Freight Forwarder",
        port_authority: "Port Authority",
        carrier: "Carrier",
        customs_broker: "Customs Broker",
        insurance_provider: "Insurance Provider",
    };
    return mapping[type] || type;
}

export function mapOnboardingStatusToDisplay(status: OnboardingStatus): string {
    const mapping: Record<OnboardingStatus, string> = {
        pending: "Pending",
        kyc_in_progress: "KYC In Progress",
        kyc_completed: "KYC Completed",
        active: "Active",
        suspended: "Suspended",
        churned: "Churned",
    };
    return mapping[status] || status;
}

